import React, { useEffect, useRef, useState } from 'react';
import type { ComboOption } from './Combo';
import { useEsMovil } from '../hooks/useEsMovil';

/**
 * Una opción puede venir como `string` (atajo: value === label, lo usa el
 * filtro de Estatus con `ESTATUS_OPTIONS`) o como `ComboOption` cuando el
 * value y el texto visible difieren (ej. Tienda: `VAZZA#8` -> "AZCAPOTZALCO ·
 * VAZZA").
 */
export type MultiComboOption = string | ComboOption;

export interface MultiComboProps {
  label: string;
  options: MultiComboOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  /**
   * Variante "flat" (spec §2): para vivir DENTRO del panel de filtros sin
   * verse como card-sobre-card (sin fondo/borde/sombra, radio 9px, padding
   * compacto). Mismo comportamiento; solo cambia el estilo del botón.
   */
  flat?: boolean;
  /** Mismo acento azul del `Combo` (Regional/Supervisor lo usan). */
  accent?: boolean;
  /**
   * Base de los `data-testid` (`multicombo-<testid>` en el botón,
   * `<testid>-opt-<value>` en cada fila). Default `estatus` para no romper las
   * pruebas del filtro de Estatus, que nació con estos ids hardcodeados.
   */
  testid?: string;
  /** Texto del botón y de la fila "limpiar" cuando no hay nada elegido. */
  allLabel?: string;
  /**
   * A partir de cuántas opciones se muestra el buscador. Default 8 — Tienda
   * tiene ~159 opciones y sin búsqueda es inusable; Estatus tiene 4 y no lo
   * necesita.
   */
  searchThreshold?: number;
}

function normalize(opt: MultiComboOption): ComboOption {
  return typeof opt === 'string' ? { value: opt, label: opt } : opt;
}

/**
 * Dropdown multi-select compacto (checkboxes), con el mismo lenguaje visual de
 * `Combo.tsx` (botón + panel flotante + backdrop `position:fixed` que cierra
 * al hacer clic fuera) pero para elegir 0..N valores en vez de uno solo.
 *
 * Lo usan el filtro "Estatus" de producto (PT5) y —desde 2026-08-03, punto 3
 * del usuario— los 4 filtros de la cascada del Global (Regional · Supervisor ·
 * Cadena · Tienda).
 *
 * Comportamiento: el botón muestra `allLabel` cuando `value` viene vacío, la
 * etiqueta del único elegido cuando hay exactamente 1, o "N seleccionados"
 * cuando hay 2 o más. Al abrir, la lista trae una fila `allLabel` (limpia la
 * selección completa) + una fila por opción con checkbox — togglear NO cierra
 * el panel, para poder marcar varias seguidas; lo cierran un clic fuera o
 * Escape. Con más de `searchThreshold`
 * opciones aparece un buscador (contains, case-insensitive) idéntico al de
 * `Combo`.
 */
const MultiCombo: React.FC<MultiComboProps> = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  flat = false,
  accent = false,
  testid = 'estatus',
  allLabel = 'Todos',
  searchThreshold = 8,
}) => {
  const esMovil = useEsMovil();
  const [open, setOpen] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  const [q, setQ] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const opts = options.map(normalize);
  const conBuscador = opts.length > searchThreshold;

  useEffect(() => {
    if (open && conBuscador) {
      inputRef.current?.focus();
    }
  }, [open, conBuscador]);

  const toggle = () => {
    setOpen((prev) => !prev);
    setQ('');
  };
  const close = () => {
    setOpen(false);
    setQ('');
  };

  // Escape cierra el panel. Togglear una opción NO lo cierra (multiselección)
  // y su capa fija tapa el resto del panel de filtros: sin esto la única salida
  // era un clic fuera, y con el teclado no había ninguna. Se escucha en el
  // documento porque el foco puede estar en el buscador, en el botón o en
  // ninguna parte.
  useEffect(() => {
    if (!open) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [open]);

  const toggleOption = (v: string) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  const clearAll = () => onChange([]);

  const buttonLabel =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? opts.find((o) => o.value === value[0])?.label ?? value[0]
        : `${value.length} seleccionados`;

  const labelColor = accent ? '#22349b' : '#9298a3';
  const borderColor = accent ? '#dfe4f2' : '#e7e9ef';

  const needle = q.toLowerCase();
  const filtered = conBuscador
    ? opts.filter((o) => o.label.toLowerCase().includes(needle))
    : opts;

  // Estilo del botón según variante (spec §2): "flat" (dentro del panel) vs
  // "normal" (suelto), mismo criterio que `Combo`.
  const buttonStyle: React.CSSProperties = flat
    ? {
        width: '100%',
        textAlign: 'left',
        background: btnHover ? '#f4f6fa' : 'transparent',
        border: 'none',
        borderRadius: 9,
        padding: '3px 2px',
        boxShadow: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'block',
      }
    : {
        width: '100%',
        textAlign: 'left',
        background: btnHover ? '#fbfcfe' : '#fff',
        border: `1px solid ${borderColor}`,
        borderRadius: 13,
        padding: '7px 13px',
        boxShadow: '0 1px 2px rgba(20,28,54,.04)',
        cursor: 'pointer',
        fontFamily: 'inherit',
        display: 'block',
      };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        data-testid={`multicombo-${testid}`}
        onClick={toggle}
        onMouseEnter={() => setBtnHover(true)}
        onMouseLeave={() => setBtnHover(false)}
        title={placeholder}
        style={buttonStyle}
      >
        <div
          style={{
            // Regla 5 de la adaptacion a movil: ninguna etiqueta por debajo
            // de 11 px. Este rotulo ("REGIONAL", "SUPERVISOR", ...) va en
            // versalitas con `letterSpacing`, que a 10 px en un telefono es lo
            // primero que se vuelve ilegible. En escritorio se queda en 10.
            fontSize: esMovil ? 11 : 10,
            letterSpacing: '.09em',
            textTransform: 'uppercase',
            color: labelColor,
            fontWeight: 800,
          }}
        >
          {label}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 3,
            gap: 8,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 14,
              color: '#14171d',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {buttonLabel}
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke={labelColor}
            strokeWidth={2.4}
            strokeLinecap="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </button>

      {open && (
        <>
          <div
            onClick={close}
            style={{ position: 'fixed', inset: 0, zIndex: 60 }}
          />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              minWidth: '100%',
              width: 'max-content',
              maxWidth: 340,
              background: '#fff',
              border: '1px solid #e2e5ee',
              borderRadius: 12,
              boxShadow: '0 20px 48px -14px rgba(20,28,54,.45)',
              zIndex: 61,
              overflow: 'hidden',
            }}
          >
            {conBuscador && (
              <div style={{ padding: '9px 10px', borderBottom: '1px solid #f0f2f6' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    background: '#f4f6fa',
                    border: '1px solid #e7e9ef',
                    borderRadius: 9,
                    padding: '7px 10px',
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9298a3"
                    strokeWidth={2.2}
                    strokeLinecap="round"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="M21 21l-4-4" />
                  </svg>
                  <input
                    ref={inputRef}
                    data-testid={`multicombo-${testid}-search`}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={placeholder}
                    style={{
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      fontFamily: 'inherit',
                      fontSize: 13,
                      width: '100%',
                      color: '#14171d',
                    }}
                  />
                </div>
              </div>
            )}
            <div
              data-testid="multicombo-options"
              style={{ maxHeight: 264, overflow: 'auto', padding: 5 }}
            >
              <div
                data-testid={`${testid}-opt-todos`}
                onClick={clearAll}
                style={{
                  padding: '8px 11px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 13.5,
                  fontWeight: value.length === 0 ? 800 : 600,
                  color: value.length === 0 ? '#22349b' : '#2b3350',
                  background: value.length === 0 ? '#eef1fb' : 'transparent',
                  borderBottom: '1px solid #f0f2f6',
                  marginBottom: 4,
                }}
              >
                {allLabel}
              </div>
              {filtered.map((o) => {
                const checked = value.includes(o.value);
                return (
                  <div
                    key={o.value}
                    data-testid={`${testid}-opt-${o.value}`}
                    onClick={() => toggleOption(o.value)}
                    style={{
                      padding: '8px 11px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 13.5,
                      fontWeight: checked ? 800 : 600,
                      color: checked ? '#22349b' : '#2b3350',
                      background: checked ? '#eef1fb' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        flex: '0 0 auto',
                        borderRadius: 4,
                        border: checked ? 'none' : '1.5px solid #ccd1db',
                        background: checked ? '#22349b' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {checked && (
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#fff"
                          strokeWidth={3}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M5 12l5 5L20 6" />
                        </svg>
                      )}
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>{o.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MultiCombo;
